/**
 * Pike's spray bay on screen. The car is the picture: the camera frames it in the free part of the screen
 * (sprayBaySystem + framing.ts read `insets()`), and the controls are a slim strip round the edges —
 *
 *  - bottom: a category bar (Paint · Finish · Wheels · Rim colour · Neon · Height · Camber · Track) and,
 *    above it, one scrolling row of swatches / option chips for that category, with the chosen option's
 *    name and price as a caption. Picking a category swings the camera to its shot (wheels → the wheel
 *    close up, neon → low and dim, stance → low from behind);
 *  - top left, one slim line: Leave and the ownership badge ("✓ Your car — kept in Lockup 7"), or for a car that
 *    is not yours one pill "! Not yours · Keep it £300" that toggles Keep this one;
 *  - top right: the cash, the bill pill (total ▾ → the itemised bill for a few seconds) and "Pay & drive out".
 * The car itself is shown in Pike's dark studio (studio.ts); the camera frames it in the free rect between the rows.
 *
 * Drag or swipe the car to look round, pinch or the wheel to zoom; ◎ goes back to the shot (⟲ ⟳ ＋ － too on a desktop).
 * Keys / pad (ui.openOverlay routes them to `onNav`): ↑ ↓ pick a zone (pay, keep, the options, the
 * categories), ← → change it, Enter pays (on pay) or moves on, Esc leaves. Q / E (or [ ]) turn the camera.
 * Touch-first: every target is at least 44 px; a selected option is a ring AND a check, never colour alone,
 * and a swatch's name is in the caption. It only draws; what the options do is the controller's.
 */
import { el, esc, html, setText, toggle } from '../ui/dom';
import type { NavAction } from '../ui/screens';
import type { ModuleOverlay } from '../ui/uiTypes';
import type { Vehicle } from '../vehicles/types';
import { CAMBERS, FINISHES, GARAGE_PAINTS, modLines, NEON_COLOURS, RIDES, RIM_COLOURS, RIMS, TRACKS, type CarMods } from '../../shared/garage';
import type { GarageView, Insets } from './framing';
import './garage.css';

export interface BayController {
  readonly title: string;
  readonly sub: string;
  touch(): boolean;
  balance(): number;
  car(): Vehicle | null;
  current(): CarMods;
  pending(): CarMods;
  beforePaint(): number;
  pendingPaint(): number | null;
  paintCost(): number;
  /** `badge` is the short form for the strip; `text` the full sentence (its tooltip / screen-reader label). */
  ownership(): { mine: boolean; text: string; badge: string; keepPrice: number | null };
  keep(): boolean;
  bill(): { label: string; price: number }[];
  total(): number;
  note(): string;
  busy(): boolean;
  set(key: string, value: unknown): boolean;
  pay(): void;
  leave(): void;
  orbit(dYaw: number, dPitch: number): void;
  zoom(factor: number): void;
  resetView(): void;
  /** The gang livery on offer (online, once the gang has bought it; WP-X4), or null: no Livery category. */
  livery(): { colour: number; fee: number; off: number } | null;
  /** The category on screen (the camera's shot), and switching it. */
  currentView(): GarageView;
  view(v: GarageView): void;
  sound(s: 'tick' | 'click' | 'confirm' | 'back' | 'whoosh' | 'denied'): void;
}

/** The categories, in the bar's order: the option they set and how the chips name it (aria "Label: option"). */
const CATS: { id: GarageView; name: string; key: string; label: string }[] = [
  { id: 'paint', name: 'Paint', key: 'paint', label: 'Colour' },
  { id: 'finish', name: 'Finish', key: 'finish', label: 'Finish' },
  { id: 'wheels', name: 'Wheels', key: 'wheels', label: 'Rims' },
  { id: 'rim', name: 'Rim colour', key: 'rim', label: 'Rim colour' },
  { id: 'neon', name: 'Neon', key: 'neon', label: 'Underglow' },
  { id: 'ride', name: 'Height', key: 'ride', label: 'Ride height' },
  { id: 'camber', name: 'Camber', key: 'camber', label: 'Camber' },
  { id: 'track', name: 'Track', key: 'track', label: 'Track width' },
  { id: 'livery', name: 'Livery', key: 'livery', label: 'Gang livery' },
];

interface Opt {
  value: unknown;
  label: string;
  /** Colour chip (hex) or none (a word chip). */
  hex?: number;
}

type Zone = 'pay' | 'keep' | 'opts' | 'cats';

const icon = (inner: string, size = 22): string =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
/** Camera and strip icons (original line drawings). */
const ICON = {
  left: icon('<path d="M19 13a7 7 0 1 1-2.1-5"/><path d="M17.5 3v5h-5"/>'),
  right: icon('<path d="M5 13a7 7 0 1 0 2.1-5"/><path d="M6.5 3v5h5"/>'),
  closer: icon('<path d="M12 5v14M5 12h14"/>'),
  further: icon('<path d="M5 12h14"/>'),
  reset: icon('<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2" fill="currentColor"/>'),
  leave: icon('<path d="M15 5l-7 7 7 7"/>', 20),
  check: icon('<path d="M5 12.5l4.5 4.5L19 7.5"/>', 14),
  more: icon('<path d="M6 9l6 6 6-6"/>', 16),
};

const pounds = (n: number): string => `£${n.toLocaleString('en-GB')}`;
const css = (hex: number): string => `#${hex.toString(16).padStart(6, '0')}`;
/** Light swatches get a dark check, dark ones a light one. */
const light = (hex: number): boolean => ((hex >> 16) & 255) * 0.3 + ((hex >> 8) & 255) * 0.59 + (hex & 255) * 0.11 > 150;

export class GarageModeScreen implements ModuleOverlay {
  readonly root: HTMLElement;
  private zone: Zone = 'opts';
  private readonly top: HTMLElement;
  private readonly own: HTMLElement;
  private readonly cash: HTMLElement;
  private readonly noteEl: HTMLElement;
  private readonly totalBtn: HTMLButtonElement;
  private readonly payBtn: HTMLButtonElement;
  private readonly billEl: HTMLElement;
  private readonly dock: HTMLElement;
  private readonly cap: HTMLElement;
  private readonly chipsEl: HTMLElement;
  private readonly catsEl: HTMLElement;
  private readonly sheen: HTMLElement;
  private readonly catBtns: HTMLButtonElement[] = [];
  /** The open category's chips (rebuilt only when the category changes; otherwise updated in place). */
  private chips: { opt: Opt; b: HTMLButtonElement; price: HTMLElement | null }[] = [];
  private chipsFor: GarageView | null = null;
  private opts: Opt[] = [];
  private keepBtn: HTMLButtonElement | null = null;
  private billOpen = false;
  private billTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly ins: Insets = { l: 0, r: 0, t: 0.06, b: 0.26 };
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinch = 0;
  private readonly onKey: (e: KeyboardEvent) => void;
  private readonly onResize: () => void;

  constructor(
    parent: HTMLElement,
    private readonly c: BayController,
  ) {
    this.root = el('div', 'gm-screen', undefined, parent);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', c.title);
    toggle(this.root, 'touch', c.touch());
    // ---- the car: orbit by drag, pinch / wheel zoom
    const stage = el('div', 'gm-stage', undefined, this.root);
    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.pinch = this.spread();
    });
    stage.addEventListener('pointermove', (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      if (this.pointers.size >= 2) {
        p.x = e.clientX;
        p.y = e.clientY;
        const d = this.spread();
        if (this.pinch > 0 && d > 0) c.zoom(this.pinch / d);
        this.pinch = d;
        return;
      }
      c.orbit(-(e.clientX - p.x) * 0.009, (e.clientY - p.y) * 0.25);
      p.x = e.clientX;
      p.y = e.clientY;
    });
    const up = (e: PointerEvent): void => {
      this.pointers.delete(e.pointerId);
      this.pinch = this.spread();
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    stage.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        c.zoom(Math.exp(Math.max(-1, Math.min(1, e.deltaY * 0.0015))));
      },
      { passive: false },
    );
    this.sheen = el('div', 'gm-sheen', undefined, this.root);

    // ---- top left: leave, who the car belongs to
    this.top = el('div', 'gm-top', undefined, this.root);
    const tl = el('div', 'gm-tl', undefined, this.top);
    // Slim pills: each button keeps a 44 px target round a smaller face (.gm-face).
    const leave = html('button', 'gm-leave', `<span class="gm-face gm-glass">${ICON.leave}<span>Leave</span></span>`, tl);
    leave.type = 'button';
    leave.setAttribute('aria-label', 'Leave');
    leave.addEventListener('click', () => {
      c.sound('back');
      c.leave();
    });
    this.own = el('div', 'gm-own', undefined, tl);
    html('div', 'gm-brand', `${esc(c.title)}<span>${esc(c.sub)}</span>`, this.top);

    // ---- top right: the cash, the bill pill and pay
    const tr = el('div', 'gm-tr', undefined, this.top);
    this.cash = el('div', 'gm-cash', '', tr);
    const pill = el('div', 'gm-pill', undefined, tr);
    this.totalBtn = el('button', 'gm-total', '', pill);
    this.totalBtn.type = 'button';
    this.totalBtn.setAttribute('aria-expanded', 'false');
    this.totalBtn.addEventListener('click', () => {
      this.showBill(!this.billOpen);
      c.sound('tick');
    });
    this.payBtn = el('button', 'gm-pay', '', pill);
    this.payBtn.type = 'button';
    this.payBtn.addEventListener('click', () => this.payNow());
    this.noteEl = el('div', 'gm-note', '', tr);
    this.noteEl.setAttribute('role', 'status');
    this.billEl = el('div', 'gm-bill', undefined, tr);

    // ---- camera buttons (left edge)
    const cam = el('div', 'gm-cam', undefined, this.root);
    const camBtn = (svg: string, aria: string, run: () => void, cls = ''): void => {
      const b = html('button', `gm-glass gm-cam-btn ${cls}`, svg, cam);
      b.type = 'button';
      b.title = aria;
      b.setAttribute('aria-label', aria);
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        run();
        c.sound('tick');
      });
    };
    camBtn(ICON.left, 'Turn left', () => c.orbit(-0.5, 0), 'gm-desk');
    camBtn(ICON.right, 'Turn right', () => c.orbit(0.5, 0), 'gm-desk');
    camBtn(ICON.closer, 'Closer', () => c.zoom(0.8), 'gm-desk');
    camBtn(ICON.further, 'Further', () => c.zoom(1.25), 'gm-desk');
    camBtn(ICON.reset, 'Back to this view', () => c.resetView());

    // ---- the strip: caption, option chips, categories
    this.dock = el('div', 'gm-dock', undefined, this.root);
    this.cap = el('div', 'gm-cap', undefined, this.dock);
    this.cap.setAttribute('aria-live', 'polite');
    this.chipsEl = el('div', 'gm-chips', undefined, this.dock);
    this.chipsEl.setAttribute('role', 'radiogroup');
    this.chipsEl.dataset.touchScroll = '';
    this.catsEl = el('div', 'gm-cats', undefined, this.dock);
    this.catsEl.setAttribute('role', 'tablist');
    this.catsEl.dataset.touchScroll = '';
    for (const cat of CATS) {
      const b = html('button', 'gm-cat', `<span>${esc(cat.name)}</span><i aria-hidden="true"></i>`, this.catsEl);
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.dataset.view = cat.id;
      b.addEventListener('click', () => {
        this.zone = 'opts';
        this.pickCat(cat.id);
      });
      this.catBtns.push(b);
    }
    if (!c.touch()) html('div', 'gm-keys', '<span>Drag to look round · wheel to zoom · Q / E turn</span><span>← → change · ↑ ↓ choose · Enter pay · Esc leave</span>', this.dock);

    this.onKey = (e: KeyboardEvent): void => {
      if (e.code === 'KeyQ' || e.code === 'BracketLeft') c.orbit(-0.3, 0);
      else if (e.code === 'KeyE' || e.code === 'BracketRight') c.orbit(0.3, 0);
      else if (e.code === 'Minus') c.zoom(1.15);
      else if (e.code === 'Equal') c.zoom(0.87);
      else if (e.code === 'KeyR') c.resetView();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', this.onKey);
    this.onResize = (): void => this.measure();
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  /** The part of the screen the strip covers (fractions per edge), for the camera to frame the car round it. */
  insets(): Insets {
    return this.ins;
  }

  private measure(): void {
    const r = this.root.getBoundingClientRect();
    if (r.height < 10) return;
    const d = this.dock.getBoundingClientRect();
    this.ins.b = Math.max(0, Math.min(0.6, (r.bottom - d.top) / r.height));
    // The top row: its visible pills (not their larger touch targets). The car sits wholly below them.
    let top = r.top;
    for (const f of this.top.querySelectorAll<HTMLElement>('.gm-face, .gm-brand, .gm-cash')) {
      const fr = f.getBoundingClientRect();
      if (fr.height > 0) top = Math.max(top, fr.bottom);
    }
    this.ins.t = Math.max(0, Math.min(0.3, (top - r.top + 6) / r.height));
  }

  private spread(): number {
    if (this.pointers.size < 2) return 0;
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a!.x - b!.x, a!.y - b!.y);
  }

  private payNow(): void {
    if (this.c.busy()) return;
    this.c.sound(this.c.total() > 0 && this.c.balance() < this.c.total() ? 'denied' : 'confirm');
    this.c.pay();
  }

  private showBill(on: boolean): void {
    this.billOpen = on;
    if (this.billTimer) clearTimeout(this.billTimer);
    this.billTimer = on ? setTimeout(() => this.showBill(false), 5000) : null;
    toggle(this.billEl, 'open', on);
    this.totalBtn.setAttribute('aria-expanded', String(on));
  }

  private pickCat(v: GarageView): void {
    if (this.c.currentView() !== v) {
      this.c.view(v);
      this.c.sound('tick');
    }
    this.render();
  }

  /** The open category's options and its current pick. */
  private optsFor(v: GarageView): Opt[] {
    const c = this.c;
    const cur = c.current();
    switch (v) {
      case 'paint':
        return [{ value: null, label: 'As it is', hex: c.beforePaint() }, ...GARAGE_PAINTS.map((s) => ({ value: s.hex, label: s.name, hex: s.hex }))];
      case 'finish': {
        const fin: Opt[] = FINISHES.map((f) => ({ value: f.id, label: f.name }));
        if (cur.finish === null) fin.unshift({ value: null, label: 'As it came' });
        return fin;
      }
      case 'wheels': {
        const own: Opt = cur.wheels !== null && !RIMS.some((r) => r.id === cur.wheels) ? { value: cur.wheels, label: 'As they are' } : { value: null, label: 'As it came' };
        const wheels: Opt[] = [own, ...RIMS.map((r) => ({ value: r.id, label: r.name }))];
        if (own.value !== null) wheels.splice(1, 0, { value: null, label: 'Factory' });
        return wheels;
      }
      case 'rim':
        return [{ value: null, label: 'Own finish' }, ...RIM_COLOURS.map((s) => ({ value: s.hex, label: s.name, hex: s.hex }))];
      case 'neon':
        return [{ value: null, label: 'Off' }, ...NEON_COLOURS.map((s) => ({ value: s.hex, label: s.name, hex: s.hex }))];
      case 'ride':
        return RIDES.map((r) => ({ value: r.id, label: r.name }));
      case 'camber':
        return CAMBERS.map((x) => ({ value: x.id, label: x.name }));
      case 'track':
        return TRACKS.map((t) => ({ value: t.id, label: t.name }));
      case 'livery': {
        const lo = c.livery();
        return [{ value: null, label: 'None' }, ...(lo ? [{ value: lo.colour, label: 'Gang colours', hex: lo.colour }] : [])];
      }
    }
  }

  private valueOf(key: string): unknown {
    return key === 'paint' ? this.c.pendingPaint() : this.c.pending()[key as keyof CarMods];
  }

  /** On the car now (what it came in with). */
  private isNow(key: string, value: unknown): boolean {
    return key === 'paint' ? value === null || value === this.c.beforePaint() : this.c.current()[key as keyof CarMods] === value;
  }

  /** What this option adds to the bill, against what is on the car now. */
  private priceOf(key: string, value: unknown): number {
    if (this.isNow(key, value)) return 0;
    if (key === 'paint') return this.c.paintCost();
    if (key === 'livery') {
      const lo = this.c.livery();
      return lo ? (value === null ? lo.off : lo.fee) : 0;
    }
    const cur = this.c.current();
    return modLines(cur, { ...cur, [key]: value } as CarMods)[0]?.price ?? 0;
  }

  /** A short line about the open category (the respray's rules, the rubbing rule). */
  private hintFor(v: GarageView): string {
    const p = this.c.pending();
    switch (v) {
      case 'paint':
        return `a new colour is a respray — new plates, repaired, heat off`;
      case 'neon':
        return 'faint by day, a proper glow after dark';
      case 'track':
        return p.ride < 0 && p.track < 0 ? 'lowered on tucked wheels would rub: they sit at stock width' : 'looks only — it drives the same';
      case 'camber':
      case 'ride':
        return 'looks only — it drives the same';
      case 'livery':
        return 'your gang’s colour on the stripes, roof or band — looks only';
      default:
        return '';
    }
  }

  private buildChips(v: GarageView): void {
    const cat = CATS.find((x) => x.id === v)!;
    this.opts = this.optsFor(v);
    this.chipsEl.textContent = '';
    this.chipsEl.setAttribute('aria-label', cat.label);
    this.chips = this.opts.map((o) => {
      const sw = o.hex !== undefined;
      const b = el('button', sw ? 'gm-chip gm-swatch' : 'gm-chip', undefined, this.chipsEl);
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-label', `${cat.label}: ${o.label}`);
      b.title = o.label;
      let price: HTMLElement | null = null;
      if (sw) {
        b.style.setProperty('--sw', css(o.hex!));
        toggle(b, 'light', light(o.hex!));
        html('span', 'gm-tick', ICON.check, b);
      } else {
        html('span', 'gm-chip-name', `${ICON.check}${esc(o.label)}`, b);
        price = el('span', 'gm-chip-price', '', b);
      }
      b.addEventListener('click', () => {
        this.zone = 'opts';
        this.pick(cat.key, o.value);
      });
      return { opt: o, b, price };
    });
    this.chipsFor = v;
  }

  private pick(key: string, value: unknown): void {
    if (!this.c.set(key, value)) return;
    this.c.sound('tick');
    // A quick sheen over the car when the paint or the finish changes; a little pop on the chip.
    if (key === 'paint' || key === 'finish' || key === 'rim') {
      this.sheen.classList.remove('go');
      void this.sheen.offsetWidth;
      this.sheen.classList.add('go');
    }
    const hit = this.chips.find((x) => x.opt.value === value);
    if (hit) {
      hit.b.classList.remove('pop');
      void hit.b.offsetWidth;
      hit.b.classList.add('pop');
    }
  }

  render(): void {
    const c = this.c;
    const v = c.currentView();
    const cat = CATS.find((x) => x.id === v) ?? CATS[0]!;
    const p = c.pending();
    const cur = c.current();
    // ownership badge
    const own = c.ownership();
    this.own.textContent = '';
    toggle(this.own, 'mine', own.mine);
    this.keepBtn = null;
    if (own.keepPrice === null) {
      // One small badge: "✓ Your car — kept in Lockup 7" (or "! Not yours" where Pike can't make it yours).
      const badge = html('span', 'gm-badge', `<span class="gm-face gm-glass"><b aria-hidden="true">${own.mine ? '✓' : '!'}</b><span>${esc(own.badge)}</span></span>`, this.own);
      badge.title = own.text;
      badge.setAttribute('aria-label', own.text);
    } else {
      // Not yours: the badge and "Keep this one" are one pill — "! Not yours · Keep it £300" (✓ Keeping it, ticked).
      const k = html(
        'button',
        'gm-keep',
        `<span class="gm-face gm-glass"><b aria-hidden="true">${c.keep() ? '✓' : '!'}</b><span>${c.keep() ? 'Keeping it' : `${esc(own.badge)}<i>·</i>Keep it`}</span><em>${pounds(own.keepPrice)}</em></span>`,
        this.own,
      );
      k.title = own.text;
      k.setAttribute('aria-label', `${own.text} Keep this one: ${pounds(own.keepPrice)}`);
      k.type = 'button';
      toggle(k, 'on', c.keep());
      toggle(k, 'kf', this.zone === 'keep');
      k.setAttribute('aria-pressed', String(c.keep()));
      k.addEventListener('click', () => {
        c.set('keep', !c.keep());
        c.sound('tick');
      });
      this.keepBtn = k;
    }
    if (!this.keepBtn && this.zone === 'keep') this.zone = 'opts';
    // categories: the open one, and a dot on each that has a change on the bill
    for (const b of this.catBtns) {
      const id = b.dataset.view as GarageView;
      // The Livery category only where Pike has the gang's livery to offer (online, WP-X4).
      b.hidden = id === 'livery' && !c.livery();
      const on = id === v;
      toggle(b, 'on', on);
      b.setAttribute('aria-selected', String(on));
      const key = CATS.find((x) => x.id === id)!.key;
      const changed = key === 'paint' ? c.pendingPaint() !== null : p[key as keyof CarMods] !== cur[key as keyof CarMods];
      toggle(b, 'changed', changed);
      b.setAttribute('aria-label', `${CATS.find((x) => x.id === id)!.name}${changed ? ' (changed)' : ''}`);
    }
    toggle(this.catsEl, 'kf', this.zone === 'cats');
    // chips
    const fresh = this.chipsFor !== v;
    if (fresh) this.buildChips(v);
    const value = this.valueOf(cat.key);
    let sel = this.chips.find((x) => x.opt.value === value) ?? this.chips[0]!;
    if (cat.key === 'paint' && value === null) sel = this.chips[0]!;
    for (const x of this.chips) {
      const on = x === sel;
      toggle(x.b, 'on', on);
      x.b.setAttribute('aria-checked', String(on));
      toggle(x.b, 'now', this.isNow(cat.key, x.opt.value));
      if (x.price) {
        const pr = this.priceOf(cat.key, x.opt.value);
        setText(x.price, this.isNow(cat.key, x.opt.value) ? 'fitted' : pounds(pr));
      }
    }
    toggle(this.chipsEl, 'kf', this.zone === 'opts');
    if (fresh) this.centre(sel.b, false);
    // caption: what's picked, what it costs
    const pr = this.priceOf(cat.key, sel.opt.value);
    const hint = this.hintFor(v);
    const price = this.isNow(cat.key, sel.opt.value) ? 'on the car' : cat.key === 'paint' ? `respray ${pounds(pr)}` : `+${pounds(pr)}`;
    this.cap.innerHTML = `<span class="gm-cap-k">${esc(cat.label)}</span><span class="gm-cap-v">${esc(sel.opt.label)}</span><span class="gm-cap-p">${esc(price)}</span>${hint ? `<span class="gm-cap-h${hint.includes('rub') ? ' warn' : ''}">${esc(hint)}</span>` : ''}`;
    // the bill
    const total = c.total();
    setText(this.cash, `Cash ${pounds(c.balance())}`);
    this.totalBtn.innerHTML = `<span class="gm-face"><span>Bill</span><b>${pounds(total)}</b>${ICON.more}</span>`;
    this.totalBtn.setAttribute('aria-label', `Bill ${pounds(total)} — show the items`);
    this.payBtn.innerHTML = `<span class="gm-face">${c.busy() ? 'Paying…' : total > 0 ? 'Pay &amp; drive out' : 'Drive out'}</span>`;
    this.payBtn.setAttribute('aria-label', c.busy() ? 'Paying' : total > 0 ? `Pay ${pounds(total)} and drive out` : 'Drive out');
    this.payBtn.disabled = c.busy();
    toggle(this.payBtn, 'short', total > c.balance());
    toggle(this.payBtn, 'kf', this.zone === 'pay');
    this.billEl.textContent = '';
    const lines = c.bill();
    if (!lines.length) el('div', 'gm-bill-none', 'Nothing on the bill yet — try something on.', this.billEl);
    for (const l of lines) html('div', 'gm-bill-line', `<span>${esc(l.label)}</span><b>${pounds(l.price)}</b>`, this.billEl);
    html('div', 'gm-bill-total', `<span>Total</span><b>${pounds(total)}</b>`, this.billEl);
    html('div', 'gm-bill-cash', `<span>Cash on you</span><b>${pounds(c.balance())}</b>`, this.billEl);
    const note = c.note() || (total > c.balance() ? `${pounds(total - c.balance())} short` : '');
    setText(this.noteEl, note);
    toggle(this.noteEl, 'on', !!note);
    this.measure();
  }

  /** Scroll a chip to the middle of its row (without moving the page). */
  private centre(b: HTMLElement, smooth: boolean): void {
    const row = this.chipsEl;
    const left = b.offsetLeft - (row.clientWidth - b.offsetWidth) / 2;
    if (typeof row.scrollTo === 'function') row.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
    else row.scrollLeft = Math.max(0, left);
  }

  /** The zones ↑ ↓ walk through, top of the screen first. */
  private zones(): Zone[] {
    return this.keepBtn ? ['pay', 'keep', 'opts', 'cats'] : ['pay', 'opts', 'cats'];
  }

  private change(dir: 1 | -1): void {
    const c = this.c;
    if (this.zone === 'cats') {
      const cats = CATS.filter((t) => t.id !== 'livery' || !!c.livery());
      const i = cats.findIndex((t) => t.id === c.currentView());
      this.pickCat(cats[(i + dir + cats.length) % cats.length]!.id);
      return;
    }
    if (this.zone === 'keep') {
      c.set('keep', !c.keep());
      c.sound('tick');
      return;
    }
    if (this.zone !== 'opts') return;
    const cat = CATS.find((x) => x.id === c.currentView())!;
    const value = this.valueOf(cat.key);
    const i = Math.max(
      0,
      this.opts.findIndex((o) => o.value === value),
    );
    const o = this.opts[(i + dir + this.opts.length) % this.opts.length]!;
    this.pick(cat.key, o.value);
    const hit = this.chips.find((x) => x.opt === o);
    if (hit) this.centre(hit.b, true);
  }

  onNav(a: NavAction): boolean {
    const zs = this.zones();
    const i = Math.max(0, zs.indexOf(this.zone));
    switch (a) {
      case 'up':
        this.zone = zs[(i - 1 + zs.length) % zs.length]!;
        this.c.sound('tick');
        this.render();
        return true;
      case 'down':
        this.zone = zs[(i + 1) % zs.length]!;
        this.c.sound('tick');
        this.render();
        return true;
      case 'left':
        this.change(-1);
        return true;
      case 'right':
        this.change(1);
        return true;
      case 'confirm':
        if (this.zone === 'pay') this.payNow();
        else if (this.zone === 'keep') this.change(1);
        else if (this.zone === 'cats') {
          this.zone = 'opts';
          this.render();
        } else {
          // On the options: on to the next category.
          this.zone = 'cats';
          this.change(1);
          this.zone = 'opts';
          this.render();
        }
        return true;
      case 'back':
        this.c.sound('back');
        this.c.leave();
        return true;
    }
    return false;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.onResize);
    if (this.billTimer) clearTimeout(this.billTimer);
    this.root.remove();
  }
}
